import React, { Suspense } from 'react';
import { lazyWithRetry, handleChunkRejectionEvent, handleChunkScriptErrorEvent } from './utils/lazyWithRetry';
import { initStaleTabSelfHeal } from './utils/staleTabSelfHeal';
import ResilientSuspenseFallback from './components/ResilientSuspenseFallback';
import BrandedTransition from './components/common/BrandedTransition';
import { logLoadingGate, LOADING_GATE_DEFAULT_TIMEOUT_MS } from './utils/loadingGate';
import { Helmet } from 'react-helmet-async';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ErrorBoundary from './components/ErrorBoundary';
import { withSafeAnimation } from './components/SafeAnimationWrapper';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './context/AuthContext';
import { useConfirmedAuth } from './hooks/useConfirmedAuth';
import { useRbac } from './hooks/useRbac';
import { useDeveloperOrg } from './hooks/useDeveloperOrg';
import { AITransactionProvider } from './context/AITransactionContext';
import { OLVRateProvider } from './context/OLVRateContext';
import { ConversationHistoryProvider } from './context/ConversationHistoryContext';
import { WalletSummaryProvider } from './context/WalletSummaryContext';
import { TopUpModalProvider } from './context/TopUpModalContext';
import { GlobalLowBalanceBanner } from './components/wallet/GlobalLowBalanceBanner';
import { PausedMembershipBanner } from './components/common/PausedMembershipBanner';
import { TrialStatusBanner } from './components/wallet/TrialStatusBanner';
import { GlobalBannerCenter } from './components/common/GlobalBannerCenter';
import EmailVerifyBanner from './components/common/EmailVerifyBanner';
import AppUpdateBanner from './components/common/AppUpdateBanner';
import InstallAppBanner from './components/common/InstallAppBanner';
import PhoneVerifyBanner from './components/common/PhoneVerifyBanner';
import { TrialOnboardingGate } from './components/onboarding/TrialOnboardingGate';
import { PendingInviteGate } from './components/onboarding/PendingInviteGate';
import { subscribeToAuthState } from './lib/auth';
import { loadUserContext } from './lib/identity/context';
import InAppMessageOverlay from './components/InAppMessageOverlay';
import DesktopSignInModalHost from './components/DesktopSignInModalHost';
import ComposioConnectCoachHost from './components/ComposioConnectCoachHost';
import EmailIntentPromptHost from './components/EmailIntentPromptHost';
import ConnectSetupHost from './components/ConnectSetupHost';
import WorkspaceTransitionHost from './components/WorkspaceTransitionOverlay';
// The chat engine loads a moment after the page paints (PR-S, 2026-09-22):
// never import it — or the chat barrel ('./components/GlobalChatSystem'),
// which re-exports the whole engine — statically here. The loader is the one
// door; scripts/check-chat-first-paint.mjs keeps it that way.
import { ChatEngineLoader } from './components/GlobalChatSystem/ChatEngineLoader';
import {
  DOCKED_COMPANION_MIN_VIEWPORT_PX,
  DOCKED_RAIL_FOOTPRINT_CSS,
} from './components/GlobalChatSystem/dockedCompanion';
import type { TokenUsageSnapshot } from './components/GlobalChatSystem/types';
import { usePageChatConfig } from './hooks/usePageChatConfig';
import { useTrialOnboardingGate } from './hooks/useTrialOnboardingGate';
import { useMaybeConversationHistory } from './context/ConversationHistoryContext';
import { useTenantChatConfig } from './hooks/useTenantChatConfig';
import { getJustJoinedOrg } from './lib/justJoinedOrg';
// Anonymous auth, the consent banner and the page-tracking hook were deleted
// 2026-09-11: the site sets no analytics or advertising cookie, so there is
// nothing to consent to. See docs/PLAN-legal-compliance-hardening-20260911.md.
import { DemoMockProvider, MoonshotPage, isMoonshotMode } from './moonshot';
import { flushAllPendingUpdates } from './utils/debouncedFirestoreUpdates';
import { installDebounceAutoFlush } from './repositories/firestoreUtils';
import { installConnectFunnelAppender } from './services/composio/connectFunnelAppender';
// Register test data cleanup utilities on window for console access
import './utils/cleanupTestWalletData';

// EnsureFixedAssets: SYNCHRONOUS import (NOT React.lazy). LandingPage was one too, until 2026-09-25.
//
// Fix for fix/unauthed-landing-page-resilient (2026-05-09 demo-day P0):
// On first mount of `/` in incognito / fresh-cache sessions, both lazy
// boundaries (EnsureFixedAssets Suspense and the Routes Suspense which
// hosts LandingPage) reliably stalled in their fallback even after
// the chunk loaded HTTP 200, the webpack module was registered
// (__webpack_modules__[182] present), and React.lazy's payload._status
// flipped to Resolved (=1). The Suspense memoizedState stayed non-null
// (suspended) and the wake-up retry never fired for the initial mount.
// Navigating away and back caused the boundary to render correctly,
// confirming the lazy itself was healthy and the bug is in the
// initial-mount Suspense retry path under React 18 production /
// concurrent rendering for THIS specific tree shape (lazy directly
// inside an outer Suspense alongside a sibling lazy + complex
// Provider stack above).
//
// LandingPage was the home page — every visitor saw it, so its bundle
// was always loaded on the first paint anyway. Code-splitting it away
// from main.* didn't actually defer download for the common case;
// it only added a Suspense boundary that triggered this retry-stall
// bug. Pulling it (and EnsureFixedAssets, which has the same shape)
// into the main chunk eliminated the failure mode entirely.
//
// 2026-09-25: prest0.ai/ shows the new design's home page (since 09-24), so
// LandingPage is only the fallback, and it left the first download. It loads
// through LandingPageDoor, which uses useDeferredModule — a plain import()
// resolved into state, never React.lazy, so the stall above cannot recur —
// exactly as the new home page's own door has served `/` since 09-24.
// tests/hooks/unauthedLandingResilient.test.ts pins both halves.
//
// Distinctive bundle marker for live-deploy verification:
//   __PREST0_UNAUTHED_LANDING_RESILIENT_2026_05_09__
import LandingPageDoor from './components/LandingPageDoor';
import EnsureFixedAssets from './components/EnsureFixedAssets';
// The new design (Miles, 2026-09-23/24): the surfaces already rebuilt swap in
// under prest0.ai/prest0design/ (the demonstration address, every surface) and
// on prest0.ai itself for the surfaces config/site-design.json lists as live.
// The door is tiny and on the first download; what it opens is its own chunk
// (the `pavilion` row of scripts/check-chat-first-paint.mjs). Where a surface
// is not shown, nothing here changes a byte of what renders.
import { designShows } from './design/previewPath';
import PavilionHomeDoor from './design/pavilion/PavilionHomeDoor';
// deferredRoute: the non-Suspense way to split a route in this tree. The four
// heaviest routes a first-time visitor never lands on (blog 414 KB, onboarding
// 301 KB, wallet 191 KB, subscription 134 KB of source) load after first paint
// instead of before it. See src/components/DeferredRoute.tsx for why this is
// not React.lazy.
import { deferredRoute } from './components/DeferredRoute';
// JusticeIsRight: SYNCHRONOUS import (NOT React.lazy) — same bug as LandingPage.
//
// Fix for fix/un-lazy-justiceisurright (2026-05-09 demo-day follow-up):
// `/justiceisurright` is the canonical chat-surface route for Fernando's
// tenant — it's the demo route. After PR #76 fixed the same
// initial-mount Suspense retry-stall for `LandingPage`, the same bug
// continued to reproduce on `/justiceisurright`: the lazy chunk loads
// HTTP 200, the webpack module is registered, but the Routes Suspense
// boundary that hosts `<SafeJusticeIsRight/>` stays in fallback forever
// on first mount. Headless playwright reproduces this 100% on a fresh
// browser context with no extensions and no auth. Navigating away to
// any other route and back to /justiceisurright lets it render — the
// classic React 18 retry-stall fingerprint described in PR #76's
// commit message.
//
// `/justiceisurright` is one of the most-visited entry points on prest0
// today (Fernando's chat surface, demo URL, marketing destination), so
// folding it into the main chunk does not regress the common-case
// first-paint cost — every relevant visitor loads it anyway. Removing
// the Suspense boundary eliminates the failure mode for this route
// without rolling back any prior PR.
//
// Distinctive bundle marker for live-deploy verification:
//   __PREST0_UNLAZY_JUSTICEISURRIGHT_2026_05_09__
import JusticeIsRight from './pages/justiceisurright';
// Header: SYNCHRONOUS import (NOT React.lazy) — demo-day follow-up to PR #76.
//
// PR #76 un-lazy'd LandingPage + EnsureFixedAssets to break the React-18
// initial-mount Suspense retry-stall on `/`. After PRs #67-79 shipped,
// the SAME stall reproduces on every other lazy route (/justiceisurright,
// /team, /mission, /about, /pricing, /security, /privacy-policy,
// /terms-of-service, /citadel, /insights). Headless playwright catches
// the watchdog "this page is taking longer than expected to load" on
// every one of those routes after a 10s wait on a fresh browser context.
//
// PR #76's diagnosis flagged that the bug fires when there are MULTIPLE
// sibling Suspense boundaries with lazy children. The remaining
// `<Suspense fallback={null}><Header/></Suspense>` boundary appears to be
// the trigger that kept the route-level Suspense from waking up on first
// mount. Header is on every page anyway, so folding it into main.* does
// not regress first-paint cost — it only removes one extra Suspense
// round-trip and unblocks the lazy route component below it.
//
// This is a workaround, not a root-cause fix. The right long-term fix is
// either upgrading React to a version that addresses the retry-stall, or
// removing the inner `<Suspense fallback={null}>` boundaries entirely.
// Pinning Header into main.* is the smallest change that unblocks the
// demo today.
//
// Distinctive bundle marker for live-deploy verification:
//   __PREST0_UNLAZY_HEADER_2026_05_09__
import Header from './components/Header';
// Public marketing/legal routes: SYNCHRONOUS imports (NOT React.lazy).
//
// PR #80 un-lazy'd Header + JusticeIsRight, but live verification still
// shows every other public lazy route stuck in the App route-level
// Suspense fallback. The safest demo-day fix is to remove React.lazy
// from the public entry routes that unauthenticated visitors can land on
// directly. Authenticated/private routes remain lazy.
//
// Live-deploy verification marker:
//   __PREST0_UNLAZY_PUBLIC_ROUTES_2026_05_09__
import TeamExpertise from './pages/TeamExpertise';
import Mission from './pages/Mission';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Cookie from './pages/Cookie';
import About from './pages/About';
import Legal from './pages/Legal';
import Trust from './pages/Trust';
import Security from './pages/Security';
// NOTE (Round M, 2026-07-15): the /citadel web page was deleted entirely per
// owner directive — its former route now falls through to the catch-all 404.
// Internal 'citadel' naming elsewhere (functions/, vm instance ids, chat
// internal-surface source patterns) refers to the internal bot VM surfaces,
// NOT the deleted web page, and stays.
import Pricing from './pages/Pricing';
import ForEnterprise from './pages/ForEnterprise';
import ForLawyers from './pages/ForLawyers';
import ForIndividuals from './pages/ForIndividuals';
import Settings from './components/Settings';
import Profile from './components/Profile';
import Organization from './pages/Organization';
// SYNC import per the authenticated-page convention (see the un-lazy markers
// above) — an authenticated account page must never be lazy.
import ActivityPage from './pages/Activity';
import UsageLimitBanner from './components/Activity/UsageLimitBanner';
import VmLifecycleBanner from './components/common/VmLifecycleBanner';
import Notifications from './pages/Notifications';
import AgentActivity from './pages/AgentActivity';
import Integrations from './pages/Integrations';
import { readConnectPopupLaunch } from './services/composio/ComposioClient';
// Tier-21 interactive 3D preview viewer (2026-08-01). SYNC import per the
// authenticated-page convention above (React-18 route-level Suspense retry stall).
import Preview3DPage from './components/FilePreview/Preview3DPage';
import FloatingPreviewProvider from './components/FilePreview/FloatingPreviewContext';
import Infrastructure from './pages/Infrastructure';
import AdminVMDashboard from './pages/AdminVMDashboard';
import PortalControls from './pages/PortalControls';
import StartTrial from './pages/StartTrial';
import Signup from './pages/Signup';
// TeamRolesPage (/settings/team): SYNCHRONOUS import (NOT React.lazy).
//
// /settings/team was the last authenticated account page still lazy-loaded
// after the 2026-05-12 "un-lazy private routes" sweep (which un-lazied
// /desktop, /wallet, /integrations, /onboarding, /subscription, …). It kept
// hitting the same React-18 initial-mount route-level Suspense retry-stall:
// the `team-roles` chunk resolves (HTTP 200, module registered) but the
// route-level Suspense boundary never wakes on first mount, so the fallback
// keeps rendering until ResilientSuspenseFallback's 30 s terminal stage shows
// "this page is taking longer than expected to load. refresh" (the exact
// symptom users reported on this page). Folding the page into main.* removes
// the only suspending child under the route-level Suspense boundary for this
// route, so the stall — and its refresh UI — can no longer fire. Matches the
// proven fix already applied to every other authenticated account route.
//
// Live-deploy verification marker:
//   __PREST0_UNLAZY_TEAM_SETTINGS_2026_07_08__
import TeamRolesPage from './components/TeamRoles/TeamRolesPage';
// Round M (2026-07-15): hard account gate for the /justiceisurright tenant
// workspace — only the allowlisted account (src/config/justiceisurrightAccess.ts)
// may view it; everyone else is redirected after auth settles.
import JusticeAccessGate from './components/JusticeAccessGate';

// ── Deferred routes ────────────────────────────────────────────────────────
// The four heaviest routes a first-time visitor never lands on: blog 414 KB,
// onboarding 301 KB, wallet 191 KB, subscription 134 KB of source. They load
// after first paint instead of before it.
//
// deferredRoute is NOT React.lazy — see src/components/DeferredRoute.tsx and
// the note at the top of this file for the 2026-05-09 initial-mount Suspense
// stall that made four PRs tear code-splitting out of this app. No Suspense
// boundary is involved here, so that retry path is never entered.
//
// `/` stays a static import and must: the pin in
// tests/hooks/unauthedLandingResilient.test.ts enforces it.
const Blog = deferredRoute(() => import('./pages/Blog'), '/insights');
const Wallet = deferredRoute(() => import('./pages/Wallet'), '/wallet');
const Subscription = deferredRoute(() => import('./pages/Subscription'), '/subscription');
const Onboarding = deferredRoute(() => import('./pages/Onboarding'), '/onboarding');

// All lazy route imports below use `lazyWithRetry` (not bare `React.lazy`)
// so that a transient ChunkLoadError on a flaky network or a rolling deploy
// is retried 3× (same URL — retries only ride out transient blips) and, if
// still failing, handed to staleTabSelfHeal's manifest-gated recovery
// reload before any user-visible failure UI fires. Strictly orthogonal to
// the staged
// ResilientSuspenseFallback fix in fix/splash-overlay-overtrigger-2026-05-10
// — this hardens the rare *real* chunk-failure case so users get
// auto-recovery before the 30 s terminal-fallback budget elapses.
// NOTE: TeamRolesPage is imported SYNCHRONOUSLY above (see the un-lazy block
// near the other account-page imports). It is intentionally NOT lazy here.
const AcceptInvitePage = lazyWithRetry(() => import(/* webpackChunkName: "accept-invite" */ './pages/AcceptInvitePage'));
const AuthActionPage = lazyWithRetry(() => import(/* webpackChunkName: "auth-action" */ './pages/AuthActionPage'));

// In-app private routes below are synchronous because the React 18 route-level Suspense retry stall was recurring on account pages.
// Only low-traffic edge routes remain lazy.

const AuthCallback = lazyWithRetry(() => import(/* webpackChunkName: "auth-callback", webpackPrefetch: true */ './pages/AuthCallback'));
const AuthPage = lazyWithRetry(() => import(/* webpackChunkName: "auth", webpackPrefetch: true */ './pages/AuthPage'));
// PRE-72: Public SMS-consent signup surface (separate from /start trial flow).
const SmsTerms = lazyWithRetry(() => import(/* webpackChunkName: "sms-terms" */ './pages/SmsTerms'));
// Header: see SYNCHRONOUS import block at top of file (with LandingPage / EnsureFixedAssets / JusticeIsRight).

// Live-bundle verification marker for fix/unauthed-landing-page-resilient.
// Grep prod main.*.js for this string to confirm the deploy actually
// contains the fix. (LandingPage and EnsureFixedAssets are imported at
// the top of this file as synchronous imports — see the comment block
// next to those imports for the full bug write-up.)
//
// We attach the marker to `window` at module-eval time so the literal
// string survives terser tree-shaking. Without this side effect, an
// unused exported constant would be dead-code-eliminated and the
// marker would never appear in the production bundle.
export const __PREST0_UNAUTHED_LANDING_RESILIENT_MARKER__ =
  '__PREST0_UNAUTHED_LANDING_RESILIENT_2026_05_09__';
try {
  (window as any).__PREST0_UNAUTHED_LANDING_RESILIENT_MARKER__ =
    __PREST0_UNAUTHED_LANDING_RESILIENT_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

// Live-bundle verification marker for fix/un-lazy-justiceisurright.
// Grep prod main.*.js for this string to confirm the fix shipped.
export const __PREST0_UNLAZY_JUSTICEISURRIGHT_MARKER__ =
  '__PREST0_UNLAZY_JUSTICEISURRIGHT_2026_05_09__';
try {
  (window as any).__PREST0_UNLAZY_JUSTICEISURRIGHT_MARKER__ =
    __PREST0_UNLAZY_JUSTICEISURRIGHT_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

// Live-bundle verification marker for fix/un-lazy-header.
export const __PREST0_UNLAZY_HEADER_MARKER__ =
  '__PREST0_UNLAZY_HEADER_2026_05_09__';
try {
  (window as any).__PREST0_UNLAZY_HEADER_MARKER__ =
    __PREST0_UNLAZY_HEADER_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

// Live-bundle verification marker for fix/un-lazy-public-routes.
export const __PREST0_UNLAZY_PUBLIC_ROUTES_MARKER__ =
  '__PREST0_UNLAZY_PUBLIC_ROUTES_2026_05_09__';
try {
  (window as any).__PREST0_UNLAZY_PUBLIC_ROUTES_MARKER__ =
    __PREST0_UNLAZY_PUBLIC_ROUTES_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

// Apply safe animation HOC to components with known animation issues
// (LandingPage gets it inside LandingPageDoor, once its chunk has arrived.)
/**
 * The new design's home page: in the demonstration tree (prest0.ai/prest0design/), and on prest0.ai
 * itself while config/site-design.json lists `home` as live (since 2026-09-24). Fixed for the life of
 * the page: a page never changes address without reloading.
 */
const SHOWS_NEW_HOME = designShows('home');

const SafeJusticeIsRight = withSafeAnimation(JusticeIsRight);
const SafeTeamExpertise = withSafeAnimation(TeamExpertise);
const SafeMission = withSafeAnimation(Mission);
const SafeBlog = withSafeAnimation(Blog);
const SafePrivacy = withSafeAnimation(Privacy);
const SafeTerms = withSafeAnimation(Terms);
const SafeCookie = withSafeAnimation(Cookie);
const SafeAbout = withSafeAnimation(About);
const SafeLegal = withSafeAnimation(Legal);
const SafeTrust = withSafeAnimation(Trust);
const SafeSecurity = withSafeAnimation(Security);
const SafeAuthPage = withSafeAnimation(AuthPage);
const SafeForEnterprise = withSafeAnimation(ForEnterprise);
const SafeForLawyers = withSafeAnimation(ForLawyers);
const SafeForIndividuals = withSafeAnimation(ForIndividuals);

// Live-bundle verification marker for un-lazying authenticated/private routes.
// This removes the recurrent route-level Suspense stall on account pages
// such as /desktop, /wallet, /integrations, /onboarding, and /subscription.
export const __PREST0_UNLAZY_PRIVATE_ROUTES_MARKER__ =
  '__PREST0_UNLAZY_PRIVATE_ROUTES_2026_05_12__';
try {
  (window as any).__PREST0_UNLAZY_PRIVATE_ROUTES_MARKER__ =
    __PREST0_UNLAZY_PRIVATE_ROUTES_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

// Live-bundle verification marker for un-lazying /settings/team (the last
// authenticated account page still lazy-loaded). Removes the recurrent
// route-level Suspense stall that surfaced the "this page is taking longer
// than expected to load. refresh" UI on the team & roles settings page.
export const __PREST0_UNLAZY_TEAM_SETTINGS_MARKER__ =
  '__PREST0_UNLAZY_TEAM_SETTINGS_2026_07_08__';
try {
  (window as any).__PREST0_UNLAZY_TEAM_SETTINGS_MARKER__ =
    __PREST0_UNLAZY_TEAM_SETTINGS_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

// Live-bundle verification marker for fix/splash-still-loading-stuck-FH-QA.
// Confirms the FH-QA-2026-05-21 fix that always removes the orphaned
// `#app-splash-still-loading` subtitle from the splash watchdog the
// moment React mounts. Grep prod main.*.js for this string to verify
// the fix shipped.
export const __PREST0_SPLASH_STILL_LOADING_CLEANUP_MARKER__ =
  '__PREST0_SPLASH_STILL_LOADING_CLEANUP_2026_05_21__';
try {
  (window as any).__PREST0_SPLASH_STILL_LOADING_CLEANUP_MARKER__ =
    __PREST0_SPLASH_STILL_LOADING_CLEANUP_MARKER__;
} catch {
  /* SSR / non-browser context — marker is purely a deploy-verification aid. */
}

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

// Docked-chat page reflow (owner directive 2026-08-24 — the app-wide
// re-center the chatModeSignal hook was prepared for). While the chat pane
// is OPEN as the docked right side panel, routed page content lays out in
// the remaining space on the left — the same reflow a narrower browser
// window produces, so centered pages give up their side white space first.
// The header, banner band and overlay hosts deliberately stay full-width.
// Keyed on the <html> attribute the one persistent chat mount stamps (see
// chatModeSignal.ts), so this shell needs no chat state of its own.
// ≤900px the docked pane becomes a near-full-width overlay (its own media
// rules in GlobalChatSystem/styles.ts) — the squeeze stands down there,
// mirroring justiceisurright's DockedContentWrapper reset.
const RoutedContentShell = styled.div`
  width: 100%;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  /* Same 0.4s as the pane's dock glide (ChatContainer right/width) so page
     and pane move as one; header's house ease keeps it platform-consistent. */
  transition: width 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media (min-width: ${DOCKED_COMPANION_MIN_VIEWPORT_PX}px) {
    html[data-prest0-chat-mode='docked'] & {
      /* Pane width + its outer inset + the gutter beside it — the ONE rail
         footprint every companion surface reserves (dockedCompanion.ts), so
         page content, the preview panel and floating windows all line up. */
      width: calc(100% - ${DOCKED_RAIL_FOOTPRINT_CSS});
    }
  }
`;

// Simple 404 Not Found component
const NotFound = () => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '4rem 2rem',
      textAlign: 'center',
      minHeight: 'calc(100vh - 160px)'
    }}>
      <h1 style={{ fontSize: '3rem', marginBottom: '1rem', color: '#333' }}>404</h1>
      <p style={{ fontSize: '1.25rem', color: '#666', marginBottom: '2rem' }}>
        The page you're looking for doesn't exist or has been moved.
      </p>
      <a 
        href="/"
        style={{
          padding: '0.75rem 1.5rem',
          background: '#4A90E2',
          color: 'white',
          borderRadius: '8px',
          textDecoration: 'none',
          fontWeight: 500,
          textTransform: 'lowercase'
        }}
      >
        Return to Home
      </a>
    </div>
  );
};

// 2026-08-15 owner incident (second occurrence): this guard used to admit on
// `isAuthenticated OR hasFirebaseUser` — the left half is the localStorage-
// cached AuthContext identity, which a signed-out visitor can still carry
// (its backing token is a client-minted random string nothing expires), so
// stale cache ALONE opened every portal page. The Firebase probe was also
// one-shot, so a mid-session sign-out never re-evaluated. Now: Firebase's
// live confirmed state (useConfirmedAuth) is the ONLY authority, fail-closed,
// with the hydration gate preserved.
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { checked, signedIn } = useConfirmedAuth();

  // Subscribe to auth events with strict data isolation
  React.useEffect(() => {
    // Initialize user data isolation manager on first load
    const initializeIsolation = async () => {
      try {
        const { userDataIsolation } = await import('./utils/userDataIsolation');
        userDataIsolation.initialize();
        console.log('✅ User data isolation initialized for protected route');
      } catch (error) {
        console.error('Failed to initialize user data isolation:', error);
      }
    };
    
    initializeIsolation();
    
    const unsub = subscribeToAuthState(async (user: { uid: string; isAnonymous: boolean } | null) => {
      if (user) {
        try {
          console.log('🔒 Loading isolated user context for UID:', user.uid);
          await loadUserContext(user.uid);
        } catch (error) {
          console.error('Failed to load user context:', error);
        }
      } else {
        console.log('🚫 No authenticated user in protected route');
      }
    });
    return () => { try { unsub(); } catch {} };
  }, []);

  // Moonshot demo mode bypasses the auth wall entirely so the scripted
  // journeys play back on any shareable URL without a login flow.
  if (isMoonshotMode()) {
    return <ErrorBoundary>{children}</ErrorBoundary>;
  }

  // Avoid redirect races by waiting until we finish checking Firebase.
  // Branded beat instead of blank frames (2026-08-09): entering /onboarding
  // or /desktop used to paint nothing while this check ran.
  if (!checked) return <BrandedTransition variant="page" title="one moment…" />;

  // Firebase-confirmed sign-in is the ONLY admission — cached identities
  // never open a portal page. Pass current path as returnUrl for after login.
  if (!signedIn) return <Navigate to="/auth" state={{ returnUrl: location.pathname + location.search }} />;

  return (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  );
};

// Simple access wall for permission-gated routes (matches the app's lowercase
// voice; no navigation side effects, so a wrong guess never bounces the user).
const RouteRestricted = ({ message }: { message: string }) => (
  <div style={{ padding: '4rem 2rem', textAlign: 'center', fontFamily: 'inherit', color: '#555' }}>
    {message}
  </div>
);

/**
 * Developer-org route gate (catalog v4): wraps developer tooling routes
 * (/admin/vms). Renders ONLY when the server confirmed the active workspace is
 * a developer org (useDeveloperOrg, fail-closed) AND the caller holds the
 * required developer permission. This is the UI half of the two-key doctrine —
 * every data callable behind these routes re-checks the developer-org gate +
 * permission server-side, so this gate can only hide chrome, never grant.
 */
const DeveloperRoute = ({ permission, children }: { permission: string; children: React.ReactNode }) => {
  const { isDeveloperOrg, loading: devLoading } = useDeveloperOrg();
  const rbac = useRbac();
  if (devLoading || rbac.loading) return <BrandedTransition variant="page" title="one moment…" />;
  // FAIL CLOSED: developer routes never render on an error or a non-developer
  // workspace — including for that workspace's owner.
  if (!isDeveloperOrg || !rbac.hasPermission(permission)) {
    return <RouteRestricted message="this page is restricted to the prest0 platform engineering workspace." />;
  }
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

/**
 * Account-permission route gate: wraps account-governance routes
 * (/portal-controls). Fails OPEN on an RBAC load error — the page's write
 * endpoints are server-guarded (account.manage) regardless, so a transient
 * error must not lock a legitimate admin out of a read-only view.
 */
const PermissionRoute = ({ permission, children }: { permission: string; children: React.ReactNode }) => {
  const rbac = useRbac();
  if (rbac.loading) return <BrandedTransition variant="page" title="one moment…" />;
  if (!rbac.error && !rbac.hasPermission(permission)) {
    return <RouteRestricted message="you don't have access to this page. ask a workspace owner or admin." />;
  }
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

/**
 * /integrations/callback: the OAuth popup's landing bypasses ProtectedRoute
 * when the popup handshake mark is present — the minimal "connected — close
 * this window" card renders no user data and self-closes, and bouncing it
 * through auth guards left the popup stranded on whatever page the guard
 * redirected to (2026-08-14 owner repro, brand-new trial user). Full-window
 * OAuth returns (no mark) keep the guarded Integrations page.
 */
const IntegrationsCallbackRoute = () => {
  const [isPopup] = React.useState(() => {
    try {
      return !!readConnectPopupLaunch();
    } catch {
      return false;
    }
  });
  if (isPopup) return <Integrations />;
  return <ProtectedRoute><Integrations /></ProtectedRoute>;
};

// A chunk file that was not a script (a pruned chunk answered with index.html and
// run as one): its SyntaxError is an early duplicate of the ChunkLoadError that
// follows, and that rejection decides whether the failure is owned (below).
// Capture phase, so it is cancelled before any other listener reports it.
window.addEventListener('error', (event) => {
  handleChunkScriptErrorEvent(event);
}, true);

// Handle global errors from Promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.warn('Unhandled promise rejection:', event.reason);

  const errorMessage = event.reason?.message || '';

  // ChunkLoadError — chunk loading failures during navigation/streaming.
  // Suppressed ONLY while a loader has signaled it owns recovery (retryImport,
  // or noteChunkRecoveryActivity for a loader that never reloads, like the chat
  // skin's door): an unconditional preventDefault here used to swallow every
  // unhandled 'Failed to fetch' / 'Unexpected token' rejection app-wide
  // (webpack surfaces the HTML-as-JS case to the REJECTION as ChunkLoadError;
  // the SyntaxError goes to the window error event — those strings never
  // belonged in this branch). The rule lives in lazyWithRetry.ts so a test
  // drives it (handleChunkRejectionEvent).
  if (handleChunkRejectionEvent(event)) return;

  // Check if this is an animation-related error that we can safely ignore
  if (
    errorMessage.includes("Cannot use 'in' operator to search for 'animation'") ||
    errorMessage.includes("Cannot set properties of undefined")
  ) {
    // Prevent the error from bubbling up
    event.preventDefault();
    console.warn('Animation-related error suppressed:', errorMessage);
  }
  // Suppress noisy internal errors from Firebase callable/Firestore that are transient
  try {
    const code = String(event?.reason?.code || event?.reason?.status || '').toLowerCase();
    const message = String(event?.reason?.message || '').toLowerCase();
    if (code === 'internal' || message.includes('internal')) {
      event.preventDefault();
      console.warn('Suppressed transient Firebase internal error');
    }
  } catch {}
});

// Log Firestore permission-denied errors for security monitoring
window.addEventListener('unhandledrejection', async (event) => {
  try {
    const reason: any = event.reason || {};
    const code: string | undefined = reason?.code || reason?.status;
    if (code === 'permission-denied') {
      try { (await import('./lib/logging/secureLogger')).logRuleDenied(null, 'unknown', 'firestore', 'unhandledrejection'); } catch {}
    }
  } catch {}
});

// Add effect to run migration once when app initializes
function initializeMigration() {
  // Run account and member ID migration
  console.log('Initializing account and member ID migration...');
  import(/* webpackChunkName: "profile-migration" */ './utils/profileManager')
    .then(mod => {
      try {
        mod.migrateAllProfileIds();
      } catch (err) {
        console.warn('Profile migration failed:', err);
      }
    })
    .catch(err => {
      console.warn('Failed to load profile migration module:', err);
    });
}

// Defer migration to post-first-paint via effect below

const AppWithProviders: React.FC = () => {
  return (
    <AuthProvider>
      <OLVRateProvider>
        <WalletSummaryProvider>
          <AITransactionProvider>
            <ConversationHistoryProvider>
              {/* TopUpModalProvider sits inside WalletSummaryProvider + OLVRateProvider
                  so the in-place top-up modal has wallet + rate context, and above
                  AppContent so every surface (incl. GlobalChatSystem) can useTopUp(). */}
              <TopUpModalProvider>
                {/* FloatingPreviewProvider: app-level multitask preview windows
                    (2026-08-02 directive) — above AppContent so chat surfaces AND
                    routed pages share one floating layer that survives navigation. */}
                <FloatingPreviewProvider>
                  <AppContent />
                </FloatingPreviewProvider>
              </TopUpModalProvider>
            </ConversationHistoryProvider>
          </AITransactionProvider>
        </WalletSummaryProvider>
      </OLVRateProvider>
    </AuthProvider>
  );
};

/**
 * AppGlobalChat — thin component that renders the single app-level chat for
 * the current route, through ChatEngineLoader: the engine itself arrives a
 * moment after the page paints (PR-S, 2026-09-22). This wrapper stays eager —
 * its setSyncPaused effect must run at first paint.
 * Lives inside AppContent so it has access to Router context.
 */
const AppGlobalChat: React.FC = () => {
  const { shouldRender, config, pageContext } = usePageChatConfig();
  const tenantChatConfig = useTenantChatConfig();
  // Task #56 workspace lock: the chat IS the workspace, and it renders on
  // marketing surfaces (/, /pricing, …) that the route gate deliberately
  // leaves reachable — so a mid-onboarding account could still chat from
  // there. Hide (never unmount — see the persistent-mount note below) while
  // onboarding is owed; the hook is fail-open, so an error can't kill chat.
  const onboardingGate = useTrialOnboardingGate();

  // Pause the global Firestore conversation-history sync on any surface
  // that has the thread switcher enabled. The thread switcher owns its
  // own per-thread localStorage persistence; running both systems at
  // once causes cross-thread message bleed (thread-B's reply gets
  // flattened into thread-A because the global history listener force-
  // syncs the single-bucket doc back into React). When threads are off,
  // global sync resumes so cross-device single-thread history still
  // works.
  const maybeHistory = useMaybeConversationHistory();
  const threadsOn = config.enableThreadSwitcher === true;
  React.useEffect(() => {
    if (maybeHistory?.setSyncPaused) {
      maybeHistory.setSyncPaused(threadsOn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadsOn]);

  // P3 (persistent mount): NEVER unmount for route reasons — excluded routes
  // render the widget in hidden mode instead, so the instance (conversation,
  // drafts, in-flight streams, thread store) survives crossing /auth or
  // /onboarding. The unmount was never the auth boundary — account switches
  // purge via app:session_reset and the uid-scoped storage re-key.

  // Tenant welcome on EVERY surface (2026-07-13, chat-surface uniformity):
  // the account-personalized welcome (written during onboarding) used to
  // apply only on /desktop//agent-activity, so the same account saw a
  // different greeting base per page. It now wins wherever it's loaded;
  // signed-out visitors (tenant config never loads) keep the per-page
  // marketing welcome from the registry.
  // Memoized (2026-08-09 greeting parity): a fresh object every render used to
  // churn composeGreeting's identity downstream, re-firing the greet-on-focus
  // effect per render and letting each tab continually re-assert its own copy.
  // Fresh team member (owner directive 2026-08-12): for a few minutes after
  // an invitation is accepted, the first message is the JOIN greeting —
  // "you're now inside <org>'s workspace" — mirroring how a new organization
  // is greeted after its own onboarding. Wins over the tenant welcome (that
  // copy addresses the org owner, not someone who just walked in the door).
  // Memoized per route-config change: a fresh object every render would
  // churn composeGreeting downstream (see the mergedConfig note below).
  const justJoined = React.useMemo(() => getJustJoinedOrg(), [config]);

  const mergedConfig = React.useMemo(() => ({
    ...((tenantChatConfig.loaded && tenantChatConfig.welcomeMessage)
      ? { ...config, welcomeMessage: { text: tenantChatConfig.welcomeMessage } }
      : config),
    // Page-declared welcome + starters (2026-08-12): a route that carries its
    // OWN welcome copy in the page context registry — today only
    // /accept-invite's invitation greeting — wins over the guest/tenant
    // fallbacks, which would otherwise always load truthy values and bury
    // it. Ordered after the tenant spread so the page copy prevails, and
    // before the just-joined overlay so that (fresher, member-specific)
    // greeting still tops everything.
    ...(config.welcomeMessage ? { welcomeMessage: config.welcomeMessage } : {}),
    ...(justJoined?.firmName
      ? {
          welcomeMessage: {
            text:
              `welcome — you're now inside ${justJoined.firmName}'s workspace. ` +
              `I'm Prest0n, your team's AI employee: I can research, draft, and handle ` +
              `day-to-day work. ask me anything to get started.`,
          },
        }
      : {}),
    // F4-A (2026-07-13): canonical one-click starter chips for the empty-thread
    // welcome. Flow on every surface once tenant config loads, independent of
    // whether the welcome *message* text was overridden (recommendation 4a).
    ...(tenantChatConfig.loaded && tenantChatConfig.welcomeStarters?.length
      ? { welcomeStarters: tenantChatConfig.welcomeStarters }
      : {}),
    // Page-declared starter chips must survive the tenant/guest spread above
    // (review 2026-08-13: ordered earlier, the invitation chips were dead —
    // every visitor got guest/account starters under the invitation
    // greeting). Only /accept-invite declares page starters today.
    ...(config.welcomeStarters?.length ? { welcomeStarters: config.welcomeStarters } : {}),
    // Memory-distilled net-new greeting suggestions (2026-07-13): flow the
    // account's suggestions into the greeting composer on every surface —
    // along with the distillation's token usage, so the greeting bubble's
    // mandatory ⓘ can attribute the AI compute that produced them.
    ...(tenantChatConfig.loaded && tenantChatConfig.suggestedAutomations?.length
      ? {
          suggestedTasks: tenantChatConfig.suggestedAutomations,
          suggestedTasksUsage:
            (tenantChatConfig.suggestedAutomationsUsage as TokenUsageSnapshot | undefined) ?? null,
        }
      : {}),
    // Multi-tab greeting parity (2026-08-09): the composer must never mint a
    // greeting from an UNSETTLED config, and every composed greeting carries
    // a rank derived from where its copy came from (see greetingRank.ts).
    welcomeReady: tenantChatConfig.loaded,
    welcomeSource: tenantChatConfig.welcomeSource,
    hidden: !shouldRender || (!onboardingGate.loading && onboardingGate.needsOnboarding),
  }), [config, tenantChatConfig, shouldRender, justJoined, onboardingGate]);

  return <ChatEngineLoader config={mergedConfig} />;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  // React Router v6 matches routes case-insensitively ('/Pricing' renders the
  // pricing page), so every layout decision must compare on the lowercase
  // path — exact-compares against location.pathname let capitalized URLs
  // render the wrong header/footer chrome (owner screenshot 2026-08-02).
  const routePath = location.pathname.toLowerCase();
  const isLandingPage = routePath === '/';

  const isJusticeIsUrrightPage = routePath === '/justiceisurright';
  const isTeamPage = routePath === '/team' || routePath === '/about/team/team-expertise';
  const isMissionPage = routePath === '/about/mission/project-mission' || routePath === '/mission';
  const isInsightsPage = routePath.startsWith('/insights');
  const isPrivacyPage = routePath === '/privacy-policy' || routePath === '/legal/privacy-policy';
  const isTermsPage = routePath === '/terms-of-service' || routePath === '/legal/terms-of-service';
  const isCookiePage = routePath === '/cookie-policy' || routePath === '/legal/cookie-policy';
  const isPricingPage = routePath === '/pricing';
  const isStartTrialPage = routePath === '/start' || routePath === '/trial';
  // PRE-72: /signup + /sms-terms are public pages with their own layouts.
  const isSignupPage = routePath === '/signup';
  const isSmsTermsPage = routePath === '/sms-terms';
  const isAuthPage = routePath === '/auth';
  // Auth email action handler (verify/reset, task #50): a focused, chrome-
  // free page like /auth itself — no header nav to wander off through mid-
  // reset (owner directive 2026-08-12).
  const isAuthActionPage = routePath === '/auth/action';
  // Invitation landing (owner directive 2026-08-12): a distraction-free
  // funnel. The page renders its own non-navigating brand mark top-left; the
  // global header's nav icons and login button must not compete with the
  // accept decision. The Prest0n chat stays (invite-aware via the page
  // context registry) — only the header chrome goes.
  const isAcceptInvitePage = routePath === '/accept-invite';
  const isProfilePage = routePath === '/profile';
  const isSettingsPage = routePath === '/settings';
  const isSecurityPage = routePath.startsWith('/security');
  const isWalletPage = routePath === '/wallet';
  const isNotificationsPage = routePath === '/notifications';
  // Onboarding is a focused, full-screen flow: no global header (the page
  // renders its own minimal top bar) so there are no mid-setup escape hatches.
  const isOnboardingPage = routePath === '/onboarding';
  const isPrivateRoute = isAuthPage || isProfilePage || isSettingsPage;
  const shouldShowHeader =
    !isLandingPage &&

    !isJusticeIsUrrightPage &&
    !isTeamPage &&
    !isMissionPage &&
    !isInsightsPage &&
    !isPrivacyPage &&
    !isTermsPage &&
    !isCookiePage &&
    !isPricingPage &&
    !isStartTrialPage &&
    !isSignupPage &&
    !isSmsTermsPage &&
    !isAuthPage &&
    !isAuthActionPage &&
    !isAcceptInvitePage &&
    !isSecurityPage &&
    !isOnboardingPage;
  // Log current route for debugging
  React.useEffect(() => {
    console.log('Current location:', location);
    console.log('Current route hash:', window.location.hash);
    console.log('Current route pathname:', window.location.pathname);
  }, [location]);

  // Reliability: Auto‑flush debounced writes on route changes
  React.useEffect(() => {
    try { flushAllPendingUpdates(); } catch {}
  }, [location.pathname]);

  // Install global auto-flush listeners once
  React.useEffect(() => {
    try { installDebounceAutoFlush(); } catch {}
  }, []);

  // Push notifications are no longer registered unconditionally on mount —
  // the permission is requested from a user's click (the notifications
  // surface owns that flow), never fired at every page load.

  // Stale-tab self-heal watcher (returning-tab reliability, 2026-09-01): on
  // wake it probes whether the RUNNING bundle is provably at risk (pruned
  // assets / retention-coupled boot age) and silently reloads behind
  // staleTabSelfHeal's guards. Renders nothing and registers no banner — a
  // mere update stays the banner's click (2026-08-20 doctrine).
  React.useEffect(() => initStaleTabSelfHeal(), []);

  // Connect-funnel sink (plan P0 #8, 2026-09-02): the ONE production
  // consumer of logConnectFunnel — batches every funnel record to the
  // server day-row (ops/connectFunnel). Installed once; never load-bearing.
  React.useEffect(() => {
    try {
      return installConnectFunnelAppender();
    } catch {
      return undefined;
    }
  }, []);

  // App-level loading watchdog (added for fix/listen-channel-block).
  //
  // Why: a browser extension blocking the Firestore long-poll
  // (`firestore.googleapis.com/.../Listen/channel` -> ERR_BLOCKED_BY_CLIENT)
  // can leave downstream code paths waiting on a real-time subscription
  // even after one-shot hydration completes. We need diagnostic visibility
  // when this happens.
  //
  // IMPORTANT: this watchdog is SILENT / LOG-ONLY. It does NOT render any
  // user-visible failure UI. The 4 s budget is correct for a diagnostic
  // log because false-positives in DevTools / monitoring are cheap; what
  // is NOT cheap is rendering a "refresh" failure UI on healthy-but-slow
  // loads — that's the bug we fixed in
  // fix/splash-overlay-overtrigger-2026-05-10 by moving the
  // user-visible failure UI in `ResilientSuspenseFallback` from a 4 s
  // hard timer to a staged 6 s "still loading…" + 30 s terminal-refresh
  // budget (see src/components/ResilientSuspenseFallback.tsx and
  // src/utils/loadingGate.ts).
  //
  // The bundle-state-aware splash watchdog in `public/index.html` handles
  // the case where React itself never paints (chunk 404 / module-eval
  // error). This watchdog is the belt-and-suspenders for *post-paint*
  // gates: if the document is ready but the route still hasn't
  // committed visible content within 4 s, we log a high-visibility
  // warning so the issue is obvious in DevTools and so production
  // monitoring picks it up.
  React.useEffect(() => {
    let fired = false;
    const handle = window.setTimeout(() => {
      if (fired) return;
      try {
        const root = document.getElementById('root');
        const splash = document.getElementById('app-splash');
        const splashStillVisible = !!splash && !splash.classList.contains('hidden');
        const rootEmpty = !root || root.children.length === 0;

        // Detect stuck "loading…" / "loading..." surfaces anywhere in the
        // page so we can warn even when a downstream gate (not the App
        // itself) is the culprit.
        const visibleText = (document.body?.innerText || '').toLowerCase();
        const stuckOnLoading =
          visibleText.length < 200 && /(^|\s)loading[.\u2026]/.test(visibleText);

        if (rootEmpty || splashStillVisible || stuckOnLoading) {
          // eslint-disable-next-line no-console
          console.warn(
            '[loadingGate:app-watchdog] App still appears to be loading after ' +
              `${LOADING_GATE_DEFAULT_TIMEOUT_MS}ms. rootEmpty=${rootEmpty} ` +
              `splashStillVisible=${splashStillVisible} stuckOnLoading=${stuckOnLoading}. ` +
              'Common cause: a Firestore real-time subscription was blocked ' +
              '(net::ERR_BLOCKED_BY_CLIENT from a browser extension blocking ' +
              'firestore.googleapis.com/.../Listen/channel). Hydration is the source ' +
              'of truth for first paint; live subscription is best-effort only. ' +
              'Set localStorage.DEBUG_LOADING_GATE="1" and reload to see per-gate logs.',
          );
        }
      } catch {
        // Watchdog must never throw.
      }
      fired = true;
    }, LOADING_GATE_DEFAULT_TIMEOUT_MS);
    return () => window.clearTimeout(handle);
  }, []);

  // Render-time loading-gate telemetry (cheap when DEBUG_LOADING_GATE is off).
  logLoadingGate('app-content', true, '<none>');

  // Custom error handler for React components
  const handleComponentError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('Component error:', error, errorInfo);
  };

  // Defer heavy migrations until after first paint/idle time
  React.useEffect(() => {
    const scheduleIdle = (task: () => void) => {
      const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, opts?: { timeout: number }) => void);
      if (typeof ric === 'function') {
        ric(task, { timeout: 2000 });
      } else {
        setTimeout(task, 0);
      }
    };
    scheduleIdle(initializeMigration);
  }, []);

  return (
    <ErrorBoundary onError={handleComponentError}>
      <AppContainer>
        {/* Add noindex for auth and private routes */}
        {isPrivateRoute && (
          <Helmet>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
        )}
        <ScrollToTop />
        <Suspense fallback={null}>
          {shouldShowHeader && <Header />}
        </Suspense>
        {/* THE one status strip (owner directive 2026-08-14): the center
            renders a single band in normal flow directly below the header
            (pushes content down; never overlays the header) and ROTATES when
            more than one notice is eligible. The wrapped sources keep 100% of
            their eligibility/suppression logic but register with the center
            instead of stacking their own strips. ORG-USAGE-2026's guarantee
            stands: nobody gets paused without seeing it coming. */}
        <GlobalBannerCenter>
          <GlobalLowBalanceBanner />
          <TrialStatusBanner />
          <EmailVerifyBanner />
          <PhoneVerifyBanner />
          <UsageLimitBanner />
          {/* §7.10 pause freeze (owner decision 5): a suspended member always
              knows the workspace is read-only, never a silent wall. */}
          <PausedMembershipBanner />
          {/* Idle-lifecycle strip (2026-08-18): "goes to sleep soon" with a
              one-click keep-it-on, asleep with a one-click wake, and waking
              progress. Billing sleeps stay with the dunning banners. */}
          <VmLifecycleBanner />
          {/* Deploy freshness (2026-08-20): "prest0 just got better — refresh"
              when a new build ships; one click, lands exactly where they were.
              Never interrupts — the CTA waits while an answer is streaming. */}
          <AppUpdateBanner />
          {/* Mobile install offer (2026-09-08): on a phone, signed in, not yet
              installed — Android gets the native install sheet from a click,
              iOS Safari a short add-to-home-screen guide. Held back while an
              answer streams, never on the first page view, timestamped snoozes. */}
          <InstallAppBanner />
        </GlobalBannerCenter>
        {/* Trial onboarding lock-in: a mid-setup trial account is bounced
            from app surfaces back to /onboarding until the quick profile
            step completes (owner directive 2026-08-02). */}
        <TrialOnboardingGate />
        <PendingInviteGate />
        <Suspense fallback={null}>
          <EnsureFixedAssets />
        </Suspense>
        <RoutedContentShell data-docked-content-shell="">
        <Suspense fallback={<ResilientSuspenseFallback gateName="app-route-suspense" />}>
          <Routes>
            <Route
              path="/"
              element={SHOWS_NEW_HOME
                ? <PavilionHomeDoor fallback={<LandingPageDoor />} />
                : <LandingPageDoor />}
            />
            {/* /anonymous-demo removed */}
            
            {/* Business Pages */}


            <Route
              path="/justiceisurright"
              element={
                <JusticeAccessGate>
                  <SafeJusticeIsRight />
                </JusticeAccessGate>
              }
            />
            <Route path="/team" element={<SafeTeamExpertise />} />
            <Route path="/about/team/team-expertise" element={<Navigate to="/team" replace />} />
            <Route path="/about/mission/project-mission" element={<SafeMission />} />
            <Route path="/mission" element={<SafeMission />} />
            <Route path="/insights" element={<SafeBlog />} />
            <Route path="/insights/*" element={<SafeBlog />} />
            <Route path="/blog/*" element={<Navigate to="/insights" replace />} />
            <Route path="/privacy-policy" element={<SafePrivacy />} />
            <Route path="/legal/privacy-policy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/terms-of-service" element={<SafeTerms />} />
            <Route path="/legal/terms-of-service" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/cookie-policy" element={<SafeCookie />} />
            <Route path="/legal/cookie-policy" element={<Navigate to="/cookie-policy" replace />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/cookie" element={<Navigate to="/cookie-policy" replace />} />
            <Route path="/about" element={<SafeAbout />} />
            <Route path="/legal" element={<SafeLegal />} />
            {/* Trust Center (2026-09-11) — the subprocessor list and the plain
                statements a security questionnaire asks for. /trust is the name
                buyers type; /subprocessors is the one a DPA cites. */}
            <Route path="/trust" element={<SafeTrust />} />
            <Route path="/subprocessors" element={<Navigate to="/trust" replace />} />
            <Route path="/security" element={<SafeSecurity />} />
            <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
            <Route path="/activity" element={<ProtectedRoute><ActivityPage /></ProtectedRoute>} />
            {/* /citadel (and its /prest0 alias) deleted entirely (Round M,
                2026-07-15 owner directive) — both now fall to the catch-all
                404 below. */}
            <Route path="/settings/team" element={<ProtectedRoute><TeamRolesPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/desktop" element={<ProtectedRoute><AgentActivity /></ProtectedRoute>} />
            {/* /agent-activity (2026-08-19): the path every browser deep link
                mints (functions/src/browserDeepLink.ts) and every "View
                Desktop" / "open the live desktop" button opens. It was never
                routed — each of those links landed on the 404 card — while
                the page itself (AgentActivity) has carried the ?t= token gate
                and the validated ?svc= service-base override all along. */}
            <Route path="/agent-activity" element={<ProtectedRoute><AgentActivity /></ProtectedRoute>} />
            <Route path="/preview/3d/:artifactId" element={<ProtectedRoute><Preview3DPage /></ProtectedRoute>} />
            <Route path="/browse" element={<Navigate to="/desktop" replace />} />
            <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
            {/* The OAuth POPUP's landing must never bounce through auth
                guards (2026-08-14 owner repro: a brand-new trial user's
                popup got redirected off the callback before the minimal
                self-closing landing could render, and the sign-in window
                just stayed open on some other page). When the popup
                handshake mark is present, the callback renders the static
                landing directly — it shows no user data and closes itself.
                Without the mark (a full-window OAuth return), the guarded
                Integrations page renders exactly as before. */}
            <Route path="/integrations/callback" element={<IntegrationsCallbackRoute />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/start" element={<StartTrial />} />
            <Route path="/trial" element={<StartTrial />} />
            {/* PRE-72: public SMS-consent signup + dedicated SMS-terms surface
                required to unblock Twilio A2P 10DLC + Toll-Free verification. */}
            <Route path="/signup" element={<Signup />} />
            <Route path="/sms-terms" element={<SmsTerms />} />
            <Route path="/for-enterprise" element={<SafeForEnterprise />} />
            <Route path="/for-lawyers" element={<SafeForLawyers />} />
            <Route path="/for-individuals" element={<SafeForIndividuals />} />
            <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/infrastructure" element={<ProtectedRoute><Infrastructure /></ProtectedRoute>} />
            {/* Developer tooling (catalog v4): fleet dashboard is developer-org-only —
                gated client-side here and server-side in rbacDevListFleetVms. */}
            <Route path="/admin/vms" element={<ProtectedRoute><DeveloperRoute permission="developer.fleet.view"><AdminVMDashboard /></DeveloperRoute></ProtectedRoute>} />
            <Route path="/portal-controls" element={<ProtectedRoute><PermissionRoute permission="account.view"><PortalControls /></PermissionRoute></ProtectedRoute>} />

            {/* Invitation accept */}
            <Route path="/accept-invite" element={<AcceptInvitePage />} />

            {/* Authentication */}
            <Route path="/auth" element={<SafeAuthPage />} />
            {/* Branded handler for verification/reset email links (task #50). */}
            <Route path="/auth/action" element={<AuthActionPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Legacy routes — redirect to /wallet */}
            <Route path="/dashboard" element={<Navigate to="/wallet" replace />} />
            <Route path="/admin" element={<Navigate to="/wallet" replace />} />

            {/* Protected Routes */}
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/organization" element={<ProtectedRoute><Organization /></ProtectedRoute>} />

            {/* Moonshot demo route — renders the voice-enabled scripted chat */}
            <Route path="/moonshot" element={<MoonshotPage />} />
            <Route path="/moonshot/*" element={<MoonshotPage />} />

            {/* Catch all for 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </RoutedContentShell>
        {/* App-level GlobalChatSystem — one instance for all pages */}
        <AppGlobalChat />
        {/* In‑app platform messages overlay integrated with prompt bar at bottom */}
        <InAppMessageOverlay />
        {/* The large in-page desktop sign-in surface (2026-08-18): chat's
            browser lane pops it over ANY page via prest0n:desktop-signin. */}
        <DesktopSignInModalHost />
        {/* The "the window will say Composio" coach, over ANY page
            (2026-08-20): the shared connect engine dispatches
            prest0n:connect-coach around the sign-in window's lifecycle, so
            chat connects get the same coach as the Integrations page — and
            it hides when the sign-in window closes. */}
        <ComposioConnectCoachHost />
        {/* Which-inbox modal for CHAT connect clicks (C3b): the card's raw
            HTML buttons ask over the emailIntentChannel round-trip and this
            host renders the SAME EmailIntentPrompt the Integrations panel
            uses — one component, both surfaces. */}
        <EmailIntentPromptHost />
        {/* Setup-fields form for CHAT connect clicks (2026-08-24 WhatsApp
            repro): when an app needs initiation values before its sign-in
            (the WABA class), chat dispatches prest0n:connect-setup and this
            host opens the SAME SetupFieldsDialog the Integrations panel
            uses, right over the conversation. */}
        <ConnectSetupHost />
        {/* Sealed organization-change overlay (2026-08-19; pacing revised
            2026-08-20 — MIN_VISIBLE_MS floor overlapped with the work):
            every switch path announces the destination org full-screen and
            the boot continuation holds it up until the new workspace
            resolves — no mixed-org frame ever paints. One host, app-wide. */}
        <WorkspaceTransitionHost />
      </AppContainer>
    </ErrorBoundary>
  );
};

function App() {
  return (
    <DemoMockProvider>
      <AppWithProviders />
    </DemoMockProvider>
  );
}

export default App; 